import React, { useEffect, useState } from 'react';
import { Text } from 'rebass';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../Modal';
import Button from '../Button/Button';
import { Input, Label } from '../Form';
import EmailsInput from '../Form/EmailsInput';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import RichText from '../RichText/RichText';
import useNotify from '../../hooks/useNotify';
import Form from '@/components/Form/Form';
import { Customer, FETCH_CUSTOMERS } from '../Customer/CustomerPicker';
import { AsyncSelect } from '../ReactSelect';
import { getNameLikeCondition } from '../../helpers/search';

const SEND_HAZARD_SURVEY = gql`
  mutation SendHazardSurveyRequestEmail(
    $jobId: ID!
    $recipientProfileIds: [ID!]!
    $additionalEmails: [String!]!
    $subject: String!
    $body: String!
  ) {
    sendHazardSurveyRequestEmail(
      jobId: $jobId
      recipientProfileIds: $recipientProfileIds
      additionalEmails: $additionalEmails
      subject: $subject
      body: $body
    )
  }
`;

const GET_HAZARD_SURVEY_RECIPIENTS = gql`
  query HazardSurveyRecipients($jobId: ID!) {
    hazardSurveyRecipients(jobId: $jobId) {
      profileId
      name
      email
    }
  }
`;

type Props = {
  jobId: string;
  waterServiceId?: string;
  onClose: () => void;
};

const DEFAULT_SUBJECT = 'Hazard Assessment Survey Request';
const DEFAULT_BODY = `Hello [CUSTOMER NAME],

Please complete the hazard assessment survey for your water service at [SERVICE ADDRESS] (Site ID: [SITE ID]) by clicking the link below:

<a href="[SURVEY LINK]">Complete Hazard Assessment Survey</a>

If you have any questions, please contact us.

Thank you.`;

const VARIABLE_OPTIONS = [
  'SURVEY LINK',
  'CUSTOMER NAME',
  'SERVICE ADDRESS',
  'SITE ID',
];

const SendHazardSurveyModal: React.FC<Props> = ({
  jobId,
  waterServiceId,
  onClose,
}) => {
  const { showError, showSuccess } = useNotify();
  const client = useApolloClient();

  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);

  // Load the site contact to pre-select
  const { data: recipientsData } = useQuery(GET_HAZARD_SURVEY_RECIPIENTS, {
    variables: { jobId },
    fetchPolicy: 'network-only',
  });

  // Once we have the site contact profileId, load the Customer record to pre-select
  const siteContactProfileId: string | undefined =
    recipientsData?.hazardSurveyRecipients?.[0]?.profileId;

  const { data: defaultCustomerData } = useQuery<{ customers: Customer[] }>(
    FETCH_CUSTOMERS,
    {
      variables: { where: { profileId: siteContactProfileId } },
      skip: !siteContactProfileId,
    }
  );

  useEffect(() => {
    if (
      defaultCustomerData?.customers?.length &&
      selectedCustomers.length === 0
    ) {
      setSelectedCustomers(defaultCustomerData.customers);
    }
  }, [defaultCustomerData]);

  const loadCustomerOptions = async (inputValue: string) => {
    const res = await client.query<{ customers: Customer[] }>({
      query: FETCH_CUSTOMERS,
      fetchPolicy: 'network-only',
      variables: {
        serviceAddressWhere:
          waterServiceId && !inputValue ? { waterServiceId } : undefined,
        serviceAddressesRequired: !inputValue && !!waterServiceId,
        profileWhere: inputValue
          ? { name: getNameLikeCondition(inputValue) }
          : undefined,
        profileRequired: !!inputValue && !!waterServiceId,
        orderBy: inputValue ? undefined : [{ col: 'profile.name', dir: 'ASC' }],
      },
    });
    return res.data?.customers ?? [];
  };

  const [sendHazardSurvey, { loading: sending }] =
    useMutation(SEND_HAZARD_SURVEY);

  const handleSend = async () => {
    if (selectedCustomers.length === 0 && additionalEmails.length === 0) {
      showError('Please select at least one recipient.');
      return;
    }

    try {
      await sendHazardSurvey({
        variables: {
          jobId,
          recipientProfileIds: selectedCustomers.map((c) => c.profileId),
          additionalEmails,
          subject,
          body,
        },
      });
      showSuccess('Survey email sent successfully.');
      onClose();
    } catch (e: any) {
      showError(e?.message ?? 'Failed to send survey email.');
    }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Send Hazard Survey Request Email" />
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <Label>Recipients</Label>
              <AsyncSelect
                isMulti
                cacheOptions
                defaultOptions
                value={selectedCustomers}
                loadOptions={loadCustomerOptions}
                onChange={(vals) =>
                  setSelectedCustomers((vals as Customer[]) ?? [])
                }
                getOptionValue={(c: Customer) => c.profileId}
                getOptionLabel={(c: Customer) =>
                  c.profile?.name +
                  (c.accountNumber ? ` (${c.accountNumber})` : '')
                }
                placeholder="Select customers..."
                noOptionsMessage={() => null}
                menuPortalTarget={document.body}
                menuPlacement="auto"
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label htmlFor="survey-additional-emails">
                Additional Recipients
              </Label>
              <EmailsInput
                id="survey-additional-emails"
                value={additionalEmails}
                onChange={setAdditionalEmails}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label htmlFor="survey-subject">Subject</Label>
              <Input
                type="text"
                width="100%"
                id="survey-subject"
                value={subject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSubject(e.target.value)
                }
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <RichText
                label="Body"
                value={body}
                variableOptions={VARIABLE_OPTIONS}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setBody(e.target.value)
                }
                sx={{ minHeight: '180px' }}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={sending}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SendHazardSurveyModal;
